import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { Badge, Button, Card, Field, InfoTip, Input, Notice } from '@rydres/ui';
import { remindersApi } from '../lib/api.js';
import type { Reminder, ReminderPreset } from '../lib/types.js';

const daysUntil = (ymd: string) => Math.round((new Date(`${ymd}T12:00:00`).getTime() - Date.now()) / 86_400_000);
const tone = (days: number) => (days < 0 ? 'danger' : days <= 30 ? 'warn' : 'ok');

/** The compliance calendar: dates only, no documents. */
export function Reminders() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Reminder[]>([]);
  const [presets, setPresets] = useState<ReminderPreset[]>([]);
  const [editing, setEditing] = useState<Partial<Reminder> & { hint?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => remindersApi.list().then((r) => { setItems(r.reminders); setPresets(r.presets); }, () => undefined);
  useEffect(() => void load(), []);
  const unused = presets.filter((p) => p.kind !== 'custom' && !items.some((i) => i.kind === p.kind));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setError(null);
    const body = { kind: editing.kind ?? 'custom', label: editing.label, dueOn: editing.dueOn, leadDays: editing.leadDays ?? [90, 30, 7], notes: editing.notes || null, active: editing.active ?? true };
    try {
      if (editing.id) await remindersApi.update(editing.id, body);
      else await remindersApi.add(body);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    }
  };
  const remove = async (id: string) => {
    await remindersApi.remove(id).catch(() => undefined);
    setEditing(null);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings')} className="rounded-full bg-card2 p-2 px-3" aria-label="Back">‹</button>
          <h1 className="text-xl font-black">Renewals</h1>
          <InfoTip label="Renewals">Enter the expiry dates and we text you ahead of each one. Nothing is uploaded; the dates stay on your phone and here.</InfoTip>
        </div>
        <Button small onClick={() => setEditing({ kind: 'custom', leadDays: [30, 7] })}>+ Custom</Button>
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      <div className="space-y-2">
        {items.map((r) => {
          const days = daysUntil(r.dueOn);
          return (
            <button key={r.id} onClick={() => setEditing(r)} className={`flex w-full items-center justify-between rounded-2xl border border-line bg-card p-4 text-left ${r.active ? '' : 'opacity-50'}`}>
              <div>
                <div className="font-bold">{r.label}</div>
                <div className="text-xs text-muted">Due {new Date(`${r.dueOn}T12:00:00`).toLocaleDateString()} · reminders {r.leadDays.join(', ')} days before</div>
              </div>
              <Badge value={days < 0 ? `${-days}d overdue` : days === 0 ? 'today' : `${days}d`} tone={tone(days)} />
            </button>
          );
        })}
      </div>
      {unused.length > 0 && (
        <Card title="Add a renewal">
          <div className="space-y-2">
            {unused.map((p) => (
              <button key={p.kind} onClick={() => setEditing({ kind: p.kind, label: p.label, leadDays: p.leadDays, hint: p.hint })} className="w-full rounded-xl bg-card2 px-3 py-2 text-left">
                <div className="text-sm font-bold">{p.label}</div>
                <div className="text-xs text-muted">{p.hint}</div>
              </button>
            ))}
          </div>
        </Card>
      )}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center">
          <form onSubmit={save} className="w-full max-w-md space-y-3 rounded-3xl border border-line bg-card p-5">
            <div className="flex items-center gap-2 text-lg font-black">
              {editing.id ? 'Edit renewal' : 'New renewal'}
              {editing.hint && <InfoTip label="this renewal">{editing.hint}</InfoTip>}
            </div>
            <Field label="What"><Input required value={editing.label ?? ''} onChange={(e) => setEditing({ ...editing, label: e.target.value })} /></Field>
            <Field label="Expires on"><Input type="date" required value={editing.dueOn ?? ''} onChange={(e) => setEditing({ ...editing, dueOn: e.target.value })} /></Field>
            <Field label="Remind me (days before, comma separated)">
              <Input value={(editing.leadDays ?? []).join(', ')} onChange={(e) => setEditing({ ...editing, leadDays: e.target.value.split(/[,\s]+/).map(Number).filter((n) => Number.isInteger(n) && n >= 0) })} />
            </Field>
            <Field label="Note (optional)"><Input value={editing.notes ?? ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Policy number, broker, where to file" /></Field>
            {editing.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active ?? true} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" className="flex-1" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" className="flex-1">Save</Button>
            </div>
            {editing.id && <button type="button" onClick={() => void remove(editing.id!)} className="w-full text-sm text-danger">Delete</button>}
          </form>
        </div>
      )}
    </div>
  );
}
